/**
 * Platform Admin page — shown ONLY to platform super-admins (GET
 * /admin/platform/status). Lists every org across the platform with
 * Suspend/Activate and a type-slug-to-confirm Delete (deprovision). This is a
 * capability ABOVE orgs; the API enforces the platform-admin flag on every call.
 *
 * INTEGRATION: add a route in apps/web/src/app.tsx under the AppShell, e.g.
 *   <Route path="platform" element={<PlatformAdminPage />} />
 * and optionally a nav link (gated client-side on the platform status query).
 * Non-admins get a friendly "not authorized" panel instead of the table.
 */
import { ApiError } from '@fc/core'
import { Badge, Button, Card, Input, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, CheckCircle2, ServerCog, ShieldAlert, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { PageHeader } from '../../components/page-header'
import {
  activatePlatformOrg,
  deprovisionPlatformOrg,
  getPlatformStatus,
  listPlatformOrgs,
  suspendPlatformOrg,
  type PlatformOrgDto,
} from '../../lib/admin-api'

function statusVariant(status: string): 'success' | 'neutral' | 'warning' | 'danger' {
  if (status === 'active') return 'success'
  if (status === 'trial') return 'neutral'
  if (status === 'suspended') return 'danger'
  return 'neutral'
}

export function PlatformAdminPage() {
  const status = useQuery({ queryKey: ['platform-status'], queryFn: getPlatformStatus })

  if (status.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-muted">
        <Spinner /> Checking access…
      </div>
    )
  }

  if (!status.data?.isPlatformAdmin) {
    return (
      <div className="space-y-6">
        <PageHeader title="Platform Admin" />
        <Card>
          <div className="space-y-3 py-20 text-center">
            <ShieldAlert className="mx-auto size-8 text-dim" aria-hidden="true" />
            <p className="text-sm text-muted">You are not a platform administrator.</p>
          </div>
        </Card>
      </div>
    )
  }

  return <PlatformOrgsTable />
}

function PlatformOrgsTable() {
  const queryClient = useQueryClient()
  const [actionError, setActionError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PlatformOrgDto | null>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['platform-orgs'],
    queryFn: listPlatformOrgs,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['platform-orgs'] })
  const onError = (e: unknown) =>
    setActionError(e instanceof ApiError ? e.message : 'Action failed')

  const suspend = useMutation({
    mutationFn: (id: string) => suspendPlatformOrg(id),
    onSuccess: invalidate,
    onError,
  })
  const activate = useMutation({
    mutationFn: (id: string) => activatePlatformOrg(id),
    onSuccess: invalidate,
    onError,
  })
  const deprovision = useMutation({
    mutationFn: (id: string) => deprovisionPlatformOrg(id),
    onSuccess: () => {
      setDeleteTarget(null)
      invalidate()
    },
    onError,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform Admin"
        description="Manage every organization on this platform. These actions sit above org isolation."
      />

      {actionError && (
        <div
          role="alert"
          className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger"
        >
          {actionError}
        </div>
      )}

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading organizations…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load organizations.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'You may not have access.'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="space-y-3 py-20 text-center">
            <ServerCog className="mx-auto size-8 text-dim" aria-hidden="true" />
            <p className="text-sm text-muted">No organizations.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Organization</th>
                  <th className="px-5 py-3 font-semibold">Handle</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Created</th>
                  <th className="px-5 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((o) => (
                  <tr key={o.id} className="border-b border-border-soft last:border-0">
                    <td className="px-5 py-3 font-medium">{o.name}</td>
                    <td className="px-5 py-3 text-muted">{o.slug}</td>
                    <td className="px-5 py-3">
                      <Badge variant={statusVariant(o.status)}>{o.status}</Badge>
                      {o.closed && o.status !== 'suspended' && (
                        <span className="ml-2 text-xs text-dim">(closed)</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-muted">
                      {new Date(o.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1">
                        {o.status === 'suspended' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => activate.mutate(o.id)}
                            title="Activate"
                          >
                            <CheckCircle2 className="size-4 text-success" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => suspend.mutate(o.id)}
                            title="Suspend"
                          >
                            <Ban className="size-4 text-warning" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setActionError(null)
                            setDeleteTarget(o)
                          }}
                          title="Delete permanently"
                        >
                          <Trash2 className="size-4 text-danger" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {deleteTarget && (
        <DeprovisionDialog
          org={deleteTarget}
          pending={deprovision.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deprovision.mutate(deleteTarget.id)}
        />
      )}
    </div>
  )
}

function DeprovisionDialog({
  org,
  pending,
  onCancel,
  onConfirm,
}: {
  org: PlatformOrgDto
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const [confirmSlug, setConfirmSlug] = useState('')
  const matches = confirmSlug.trim().toLowerCase() === org.slug.toLowerCase()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deprovision-title"
    >
      <div className="absolute inset-0 bg-[var(--overlay)] backdrop-blur-sm" onClick={onCancel} />
      <Card className="relative z-10 w-full max-w-md p-6">
        <div className="space-y-4">
          <h2
            id="deprovision-title"
            className="flex items-center gap-2 text-lg font-semibold text-danger"
          >
            <Trash2 className="size-5" />
            Delete {org.name}
          </h2>
          <p className="text-sm text-muted">
            This permanently deletes the organization and <strong>all</strong> of its data across
            the platform. This cannot be undone. Type{' '}
            <code className="rounded bg-surface-2 px-1 py-0.5 text-fg">{org.slug}</code> to confirm.
          </p>
          <Input
            value={confirmSlug}
            onChange={(e) => setConfirmSlug(e.target.value)}
            placeholder={org.slug}
            autoComplete="off"
            aria-label="Organization handle"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="danger" disabled={!matches || pending} onClick={onConfirm}>
              {pending ? <Spinner /> : null}
              Delete forever
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
